<script setup lang="ts">
  import { useQuery } from '@vue/apollo-composable'
  import QueryAuditLogs from './graphql/QueryAll.gql'
  import QueryAuditLogsSinceLastCommit from './graphql/QuerySinceLastCommit.gql'
  import { AuditLog, AuditLogQuery, AuditLogQueryVariables } from '@/common/graphql/types'
  import AuditLogTimeline from '@/common/components/auditlog/components/AuditLogTimeline.vue'
  import { computed, ref } from 'vue'
  import ActionButton from '@/common/components/button/ActionButton.vue'
  import { useIntersectionObserver } from '@vueuse/core'
  import DataTableEmptyState from '@/common/components/datatable/DataTableEmptyState.vue'

  const props = defineProps({
    entityId: {
      type: String,
      required: false,
      default: null,
    },
    entityType: {
      type: String,
      required: false,
      default: null,
    },
    hideDatePicker: {
      type: Boolean,
      required: false,
      default: false,
    },
    sinceLastCommit: {
      type: Boolean,
      required: false,
      default: false,
    }
  })

  const perPage = 60
  const hasMore = ref(true)
  const offset = ref(0)
  const auditLogs = ref([] as AuditLog[])
  const initialized = ref(false)

  const startTime = ref<null | string>(null)

  const query = computed(() => props.sinceLastCommit ? QueryAuditLogsSinceLastCommit : QueryAuditLogs)

  const { onResult, loading } = useQuery<AuditLogQuery, AuditLogQueryVariables>(query.value, () => ({
    input: {
      limit: perPage,
      offset: offset.value,
      ignore_fields: [],
      start_time: startTime.value,
      entity_type: props.entityType,
      entity_id: props.entityId,
    }
  }), {
    fetchPolicy: 'no-cache'
  })

  const loadMoreButton = ref<HTMLButtonElement | null>(null)

  function setStartTime (value: string) {
    if (!value) {
      startTime.value = null
    } else {
      const time = new Date(value)
      startTime.value = time.toISOString()
    }

    offset.value = 0
    auditLogs.value = []
    hasMore.value = true
  }

  // Automatically load more data when the user scrolls to the bottom of the page.
  useIntersectionObserver(loadMoreButton, ([entry]) => {
    if (entry.isIntersecting && !loading.value && hasMore.value && !props.sinceLastCommit) {
      offset.value += perPage
    }
  }, {
    threshold: 0.25,
  })

  onResult((result) => {
    initialized.value = true
    auditLogs.value = auditLogs.value.concat(result.data.auditLogs as AuditLog[])

    // In the "since last commit" view, we consider the audit log to be "complete" when the last item is a commit.
    if (props.sinceLastCommit) {
      if (result.data.auditLogs.length === 0) {
        hasMore.value = false
        return
      }
      hasMore.value = result.data.auditLogs[result.data.auditLogs.length - 1]?.action !== 'gets_committed'
    } else {
      hasMore.value = result.data.auditLogs.length >= perPage
    }
  })

</script>

<template>
  <div class="-ml-16">
    <div class="max-w-3xl mx-auto">
      <Loader v-if="!initialized" class="py-24 ml-20" />

      <template v-else>
        <AuditLogTimeline :hideDatePicker="hideDatePicker" :auditLogs="auditLogs" @setStartTime="setStartTime" />

        <div class="flex ml-20 justify-center mt-8">
          <ActionButton
            v-if="hasMore"
            ref="loadMoreButton"
            :loading="loading"
            @click="offset += perPage"
          >
            {{ $t('common.actions.load_more') }}
          </ActionButton>
          <template v-else>
            <DataTableEmptyState
              v-if="offset === 0 && auditLogs.length === 0"
              class="w-full rounded"
              :class="{
                'bg-white': !sinceLastCommit,
                '-mt-12': sinceLastCommit,
              }"
            />
            <div v-else-if="!sinceLastCommit && !hasMore" class="mt-4 mb-2 text-xs text-center text-gray-600">
              {{ $t('common.states.no_more_data') }}
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

